import type { SimulatorElementValue, SimulatorItem } from '@vedalib/editor/lib/elements'
import { SimulatorNavigation } from '@vedalib/editor/lib/elements'
import { useMemo } from 'react'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import { FieldArray, FieldSegmented, FieldSelect, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { Typography } from 'components/uiKit/Typography'
import { t } from 'services/Translation'
import { chainName } from 'utils/form'

import SimulatorFormValueItem from './SimulatorFormValueItem'
import SimulatorFormValueItemAdd from './SimulatorFormValueItemAdd'
import type { SimulatorFormValueType } from './simulatorTypes'

const SHOW_AREA = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'showArea',
  defaultValue: true,
  label: t('elements.simulator.value.showArea'),
})

const ITEMS = genElementValueField({
  name: 'items',
  as: FieldArray<SimulatorItem>,
  rules: [{ max: 200 }],
  props: {
    min: 1,
    RenderItem: SimulatorFormValueItem,
    RenderAdd: SimulatorFormValueItemAdd,
  },
})

const NAVIGATION = genElementValueField({
  name: 'navigation',
  as: FieldSelect<SimulatorNavigation>,
  label: t('input.label.navigation'),
  defaultValue: SimulatorNavigation.FLEX_END,
  props: {
    fluid: true,
    options: [
      {
        label: t('input.option.left'),
        value: SimulatorNavigation.FLEX_START,
      },
      {
        label: t('input.option.center'),
        value: SimulatorNavigation.CENTER,
      },
      {
        label: t('input.option.right'),
        value: SimulatorNavigation.FLEX_END,
      },
      {
        label: t('input.option.hide'),
        value: SimulatorNavigation.NONE,
      },
    ],
  },
})

const NUMBERING = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'numbering',
  label: t('elements.simulator.value.numbering'),
  defaultValue: false,
  useConfigs: [
    ({ name }) => {
      const value = useFormData<SimulatorElementValue>(name, 1)
      return useMemo(
        () => ({ hidden: value.navigation === SimulatorNavigation.NONE }),
        [value.navigation],
      )
    },
  ],
})

const SimulatorFormValue: SimulatorFormValueType = ({ name }) => {
  return (
    <Padding bottom gap sides>
      <FormField config={chainName(name, SHOW_AREA)} />
      <FormField config={chainName(name, NAVIGATION)} />
      <FormField config={chainName(name, NUMBERING)} />
      <Typography>{t('elements.simulator.form.items')}</Typography>
      <FormField config={chainName(name, ITEMS)} />
    </Padding>
  )
}

export default SimulatorFormValue
